import { useState, useRef } from "react";
import { Building2, User, Briefcase, CheckCircle2, Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const ROLES = [{ id: "Owner", icon: User }, { id: "Broker", icon: Briefcase }, { id: "Developer", icon: Building2 }];
const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

export default function PostProperty() {
  const [role, setRole] = useState("Owner");
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", property_interest: "", budget: "" });
  const [files, setFiles] = useState([]); // {file, url, status, path}
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickFiles = async (list) => {
    const arr = Array.from(list || []).slice(0, 8);
    for (const file of arr) {
      const preview = URL.createObjectURL(file);
      const entry = { id: crypto.randomUUID(), file, preview, status: "uploading", path: "" };
      setFiles((f) => [...f, entry]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.upload(fd);
        setFiles((f) => f.map((x) => x.id === entry.id ? { ...x, status: "done", path: res.path } : x));
        toast.success(`${file.name} uploaded`);
      } catch {
        setFiles((f) => f.map((x) => x.id === entry.id ? { ...x, status: "error" } : x));
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const removeFile = (id) => setFiles((f) => f.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please enter your name and mobile"); return; }
    setSubmitting(true);
    try {
      const paths = files.filter((f) => f.status === "done").map((f) => f.path).join(", ");
      await api.createLead({
        ...form, lead_type: "post_property", source: role,
        message: `Post Property as ${role}${paths ? ` | Photos: ${paths}` : ""}`,
      });
      setDone(true);
    } catch { toast.error("Something went wrong"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <div className="text-center">
        <span className="text-sm font-bold uppercase tracking-wider text-pbs-purple">Sell / Rent</span>
        <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold text-pbs-dark">Post Your Property — <span className="text-pbs-orange">Free</span></h1>
        <p className="mt-2 text-pbs-grey">Reach serious, verified buyers across India. Zero brokerage to list.</p>
      </div>

      {done ? (
        <div className="mt-10 bg-white rounded-2xl border border-pbs-line p-10 text-center pbs-shadow">
          <CheckCircle2 size={56} className="mx-auto text-green-500" />
          <h2 className="mt-4 text-2xl font-bold text-pbs-dark">Listing request received!</h2>
          <p className="mt-2 text-pbs-grey">Our team will verify your details and get your property live shortly. A confirmation email is on its way.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 bg-white rounded-2xl border border-pbs-line p-6 sm:p-8 pbs-shadow space-y-5">
          <div>
            <label className="text-sm font-bold text-pbs-dark mb-2 block">I am a</label>
            <div className="grid grid-cols-3 gap-3">
              {ROLES.map((r) => (
                <button type="button" key={r.id} data-testid={`role-${r.id.toLowerCase()}`} onClick={() => setRole(r.id)}
                  className={`flex flex-col items-center gap-2 py-4 rounded-xl border-2 transition-colors ${role === r.id ? "border-pbs-purple bg-pbs-purple/5 text-pbs-purple" : "border-pbs-line text-pbs-grey hover:border-pbs-purple/40"}`}>
                  <r.icon size={22} /> <span className="text-sm font-semibold">{r.id}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *"><input data-testid="pp-name" value={form.name} onChange={set("name")} className="pp-input" placeholder="Your name" /></Field>
            <Field label="Mobile *"><input data-testid="pp-mobile" value={form.mobile} onChange={set("mobile")} className="pp-input" placeholder="+91 98XXX XXXXX" /></Field>
            <Field label="Email"><input data-testid="pp-email" value={form.email} onChange={set("email")} className="pp-input" placeholder="you@email.com" /></Field>
            <Field label="City">
              <select data-testid="pp-city" value={form.city} onChange={set("city")} className="pp-input">
                <option value="">Select city</option>
                {["Noida", "Greater Noida", "Noida Extension", "Yamuna Expressway", "Ghaziabad"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Property Type">
              <select data-testid="pp-type" value={form.property_interest} onChange={set("property_interest")} className="pp-input">
                <option value="">Select</option>{["Apartment", "Villa", "Plot", "Commercial", "Farm Land"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Expected Price"><input data-testid="pp-budget" value={form.budget} onChange={set("budget")} className="pp-input" placeholder="e.g. ₹1.5 Cr" /></Field>
          </div>

          {/* Real photo/brochure upload */}
          <div>
            <label className="text-sm font-bold text-pbs-dark mb-2 block">Property Photos / Brochure</label>
            <button type="button" data-testid="pp-upload-btn" onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-pbs-line rounded-xl p-6 text-center text-pbs-grey hover:border-pbs-purple hover:bg-pbs-purple/5 transition-colors">
              <Upload size={24} className="mx-auto mb-2 text-pbs-purple" />
              <p className="text-sm font-semibold text-pbs-dark">Click to upload photos or brochure</p>
              <p className="text-xs mt-0.5">JPG, PNG, WEBP or PDF · up to 8 files</p>
            </button>
            <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden data-testid="pp-file-input"
              onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
            {files.length > 0 && (
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                {files.map((f) => (
                  <div key={f.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border border-pbs-line bg-pbs-bg flex items-center justify-center">
                      {f.file.type.startsWith("image/") ? <img src={f.preview} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={22} className="text-pbs-grey" />}
                      {f.status === "uploading" && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 size={18} className="animate-spin text-pbs-purple" /></div>}
                    </div>
                    <button type="button" onClick={() => removeFile(f.id)} className="absolute -top-1.5 -right-1.5 bg-pbs-coral text-white rounded-full w-5 h-5 flex items-center justify-center shadow"><X size={11} /></button>
                    <p className="text-[10px] text-pbs-grey truncate mt-0.5">{f.file.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" data-testid="pp-submit" disabled={submitting}
            className="w-full py-3.5 pbs-gradient text-white font-bold rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting ? <Loader2 size={17} className="animate-spin" /> : null} Submit Listing
          </button>
          <style>{`.pp-input{width:100%;border:1px solid #E4EBE4;border-radius:10px;padding:10px 12px;font-size:14px;outline:none;background:#fff;color:#25352C}.pp-input:focus{border-color:#236B45;box-shadow:0 0 0 3px rgba(35,107,69,0.1)}`}</style>
        </form>
      )}
    </div>
  );
}
const Field = ({ label, children }) => (<label className="block"><span className="text-xs font-semibold text-pbs-dark/70 mb-1 block">{label}</span>{children}</label>);
